import React, { useEffect } from 'react';
import { Button, Callout, Icon } from '../ui';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import LocationSelector, { LocationData } from '../LocationSelector';

const addressSchema = z.object({
  street: z.string().min(1, 'Street address is required').min(3, 'Street address must be at least 3 characters').max(200, 'Street address is too long'),
  houseNo: z.string().optional().refine((val) => !val || val.length <= 20, 'House number is too long'),
  zipCode: z.string().min(1, 'Zip code is required').max(20, 'Zip code is too long').regex(/^[A-Za-z0-9\s-]{1,20}$/, 'Invalid zip code format'),
  country_id: z.number().nullable().refine((val) => val !== null && val > 0, 'Please select a country'),
  state_id: z.number().nullable().optional(),
  city_id: z.number().nullable().optional(),
  city_other: z.string().optional()
    .refine((val) => !val || val.length >= 2, 'City name must be at least 2 characters')
    .refine((val) => !val || val.length <= 100, 'City name is too long'),
}).refine((data) => data.city_id || (data.city_other && data.city_other.trim().length > 0), {
  message: 'Please select or enter a valid city',
  path: ['city_id'],
});

export type AddressFormValues = z.infer<typeof addressSchema>;

export interface AddressData {
  street: string;
  houseNo?: string;
  zipCode?: string;
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;
}

interface AddressOnlyFormProps {
  initialData?: Partial<AddressData>;
  onSubmit: (data: AddressFormValues) => void;
  isSubmitting?: boolean;
  formError?: string | null;
  isRTL?: boolean;
}

const AddressOnlyForm: React.FC<AddressOnlyFormProps> = ({
  initialData,
  onSubmit,
  isSubmitting = false,
  formError,
  isRTL = false,
}) => {
  const { register, handleSubmit, formState: { errors, touchedFields }, setValue, watch, trigger } = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { street: '', houseNo: '', zipCode: '', country_id: null, state_id: null, city_id: null, city_other: '' },
  });

  useEffect(() => {
    if (initialData) {
      setValue('street', initialData.street || '');
      setValue('houseNo', initialData.houseNo || '');
      setValue('zipCode', initialData.zipCode || '');
      setValue('country_id', initialData.country_id || null);
      setValue('state_id', initialData.state_id || null);
      setValue('city_id', initialData.city_id || null);
      setValue('city_other', initialData.city_other || '');
    }
  }, [initialData, setValue]);

  const handleLocationChange = (locationData: LocationData) => {
    setValue('country_id', locationData.country_id === null ? null : Number(locationData.country_id));
    setValue('state_id', locationData.state_id === null ? null : Number(locationData.state_id));
    setValue('city_id', locationData.city_id === null ? null : Number(locationData.city_id));
    setValue('city_other', locationData.city_other || '');
  };

  const onFormSubmit = async (data: AddressFormValues) => {
    try {
      const isFormValid = await trigger();
      if (!isFormValid) {
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField) {
          const el = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
          el?.focus();
        }
        return;
      }
      onSubmit(data);
    } catch (error) {
      console.error('Address form submission error:', error);
    }
  };

  return (
    <div>
      {formError && (
        <Callout tone="warn" style={{ marginBottom: 20, fontSize: 13 }}>
          <strong>Error:</strong> {formError}
        </Callout>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)} autoComplete="off">
        <div style={{ padding: 20, border: '1px solid var(--line)', borderRadius: 10, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Icon name="mapPin" size={16} style={{ color: 'var(--brand-blue)' }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--brand-blue)' }}>Address Information</h3>
          </div>

          <div style={{ marginBottom: 16 }}>
            <LocationSelector
              value={{
                country_id: watch('country_id') || undefined,
                state_id: watch('state_id') || undefined,
                city_id: watch('city_id') || undefined,
                city_other: watch('city_other') || '',
              }}
              onChange={(locationData) => {
                handleLocationChange(locationData);
                setTimeout(() => trigger(['country_id', 'city_id', 'city_other']), 100);
              }}
              errors={{
                country_id: errors.country_id?.message || (touchedFields.country_id && !watch('country_id') ? 'Country is required' : undefined),
                city_id: errors.city_id?.message || (touchedFields.city_id && !watch('city_id') && !watch('city_other') ? 'City is required' : undefined),
                city_other: errors.city_other?.message,
              }}
              isRTL={isRTL}
            />
          </div>

          <div className="addr-grid-3">
            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4, whiteSpace: 'nowrap' }}>
                Street Address <span style={{ color: 'var(--err)' }}>*</span>
              </label>
              <input
                className={`input${errors.street ? ' input-error' : ''}`}
                placeholder="Enter street address"
                {...register('street', { onChange: () => trigger('street') })}
              />
              {errors.street && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.street.message}</p>}
            </div>

            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4, whiteSpace: 'nowrap' }}>
                House/Bldg No.
              </label>
              <input
                className="input"
                placeholder="Apt, Suite"
                {...register('houseNo')}
              />
            </div>

            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4, whiteSpace: 'nowrap' }}>
                Zip/Postal Code <span style={{ color: 'var(--err)' }}>*</span>
              </label>
              <input
                className={`input${errors.zipCode ? ' input-error' : ''}`}
                placeholder="12345"
                {...register('zipCode', { onChange: () => trigger('zipCode') })}
              />
              {errors.zipCode && <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--err)' }}>{errors.zipCode.message}</p>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
            <Icon name="check" size={16} style={{ marginRight: 8 }} />
            {isSubmitting ? 'Processing...' : 'Validate Address'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddressOnlyForm;
